import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Grid,
  Divider,
  Container,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import seoService from './seo.service';
import type { CreateMetaTagRequest } from './seo.dto';

import { useToast } from '../../contexts/ToastContext';
const AddSEO = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateMetaTagRequest>({
    page_slug: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  });

  const handleInputChange = (
    field: keyof CreateMetaTagRequest,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      showError('Authentication required');
      return;
    }

    if (!formData.page_slug || !formData.meta_title) {
      showError('Page slug and meta title are required');
      return;
    }

    try {
      setSaving(true);
      await seoService.createMetaTag(token, formData);

      showSuccess('Meta tag created successfully');

      // Navigate back to SEO list after a short delay
      setTimeout(() => {
        navigate('/seo');
      }, 1500);
    } catch (err) {
      console.error('Error creating meta tag:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to create meta tag'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/seo');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton onClick={() => navigate('/seo')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Add color="primary" sx={{ mr: 1 }} />
            <Typography variant="h4" component="h1">
              Add New Meta Tag
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Create a new SEO meta tag with optimized content
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Page Information Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Page Information
                </Typography>
                <TextField
                  fullWidth
                  required
                  label="Page Slug"
                  value={formData.page_slug}
                  onChange={e => handleInputChange('page_slug', e.target.value)}
                  placeholder="e.g., home, about-us"
                  helperText="Unique page identifier (lowercase, hyphens)"
                  margin="normal"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* SEO Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        SEO Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Meta Title"
                        value={formData.meta_title}
                        onChange={e =>
                          handleInputChange('meta_title', e.target.value)
                        }
                        helperText="50-60 characters recommended"
                        inputProps={{ maxLength: 60 }}
                        placeholder="e.g., Clear Scan - Medical Diagnostic Services"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Meta Description"
                        multiline
                        rows={3}
                        value={formData.meta_description}
                        onChange={e =>
                          handleInputChange('meta_description', e.target.value)
                        }
                        helperText="150-160 characters recommended"
                        inputProps={{ maxLength: 160 }}
                        placeholder="Brief description of the page content..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Keywords
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Meta Keywords"
                        value={formData.meta_keywords}
                        onChange={e =>
                          handleInputChange('meta_keywords', e.target.value)
                        }
                        placeholder="keyword1, keyword2, keyword3"
                        helperText="Comma-separated keywords"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <SaveIcon />
                      }
                      disabled={
                        saving || !formData.page_slug || !formData.meta_title
                      }
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Creating...' : 'Create Meta Tag'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AddSEO;
